import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState, type FocusEvent } from 'react';
import { useApplicationStore } from '../store/application.store';
import { useNavigationStore } from '../store/navigation.store';
import { useFormContent } from '../hooks/use-form-content.hook';

import { FormNavigation } from './navigation/FormNavigation';
import { FormSection } from './FormSection';
import { logger } from '@/features/help/utils/logger';
import { ApplicationSidebar } from './sidebar/ApplicationSidebar';
import { MobileProgressHeader } from './navigation/MobileProgressHeader';
import { TermsConsentField } from './fields/TermsConsentField';
import { ContactConsentField } from './fields/ContactConsentField';
import { uploadBankStatements } from '../api/document-upload.api';
import { toast } from 'react-hot-toast';
import { SignersModal } from './misc/SignersModal';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { fileService } from '../services/file.service';
import { getApplicationBrandTheme } from '../utils/brand-theme.utils';

type DocUploadFileStatus = 'pending' | 'success' | 'error';

interface DocUploadFile {
  name: string;
  sizeBytes: number;
  status: DocUploadFileStatus;
  errorMessage?: string;
}

interface DocUploadAttempt {
  status: 'idle' | 'uploading' | 'done';
  files: DocUploadFile[];
  successCount: number;
  failureCount: number;
}

const defaultDocUploadAttempt: DocUploadAttempt = {
  status: 'idle',
  files: [],
  successCount: 0,
  failureCount: 0,
};

// Purpose: Main component that handles form content rendering with sidebar layout
export const FormContent = () => {
  const { mappingShortId } = useParams<{ mappingShortId: string }>();
  const {
    structure,
    viewableFields,
    termsConsent,
    files,
    crmId,
    userShortId,
    pdfOnly,
    legalContext,
    handleFieldValueChange,
  } = useApplicationStore();
  const { activePage, getCurrentPage, getVisiblePages } = useNavigationStore();

  // Animation state
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [hasOwnershipFieldBlurred, setHasOwnershipFieldBlurred] = useState(false);
  const [isOwnershipFieldFocused, setIsOwnershipFieldFocused] = useState(false);
  const [docUploadAttempt, setDocUploadAttempt] =
    useState<DocUploadAttempt>(defaultDocUploadAttempt);

  // Use custom hook for all form content logic
  const {
    isTermsInvalid,
    handleTermsConsentChange,
    contactConsent,
    isContactConsentInvalid,
    handleContactConsentChange,
    handleNextPageWithValidation,
    handlePreviousPageNavigation,
    formRef,
    isOwnershipPage,
    isPrimarySigner,
    shouldShowOwnershipWarning,
    showSignersModal,
    openSignersModal,
    closeSignersModal,
    additionalSigners,
    isLoadingSigners,
    hasFetchedAdditionalSigners,
    addOptimisticSigners,
  } = useFormContent({ mappingShortId });

  // Trigger animation on mount and page changes
  useEffect(() => {
    setIsVisible(false); // Reset animation

    // Scroll to top on page navigation (but not on initial load)
    if (!isInitialLoad) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const timer = setTimeout(
      () => {
        setIsVisible(true);
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      },
      isInitialLoad ? 50 : 150
    ); // Longer delay for page transitions
    return () => clearTimeout(timer);
  }, [activePage, isInitialLoad]); // Re-run when activePage changes

  const currentPage = getCurrentPage();
  const visiblePages = getVisiblePages();

  // Application pages exclude the document upload page for navigation purposes
  const applicationPages = visiblePages.filter((page) => page.id !== 'document-upload');
  const applicationTotalPages = applicationPages.length;

  const isLastApplicationPage =
    !!currentPage && applicationPages[applicationPages.length - 1]?.id === currentPage.id;
  const isDocumentUploadPage = currentPage?.id === 'document-upload';
  // Purpose: Derive active additional owners for counts and modal seed data.
  const activeAdditionalOwnerSigners = additionalSigners.filter(
    (signer) => signer.signer_index > 1 && signer.status !== 'deactivated'
  );
  const activeAdditionalOwnersCount = activeAdditionalOwnerSigners.length;
  const hasPrefetchedSigners = hasFetchedAdditionalSigners || isLoadingSigners;

  // Check if this is the last page of the application (Signatures page)
  const isLastPage = isLastApplicationPage;
  const isFirstPage = activePage === 0;
  const isPostUploadView = isDocumentUploadPage && docUploadAttempt.status !== 'idle';
  const isUploadAttemptLoading = docUploadAttempt.status === 'uploading';
  const shouldRenderOwnershipWarning =
    shouldShowOwnershipWarning && hasOwnershipFieldBlurred && !isOwnershipFieldFocused;

  useEffect(() => {
    if (!isDocumentUploadPage && docUploadAttempt.status !== 'idle') {
      setDocUploadAttempt(defaultDocUploadAttempt);
    }
  }, [isDocumentUploadPage, docUploadAttempt.status]);

  // Only surface the ownership warning after the field has blurred.
  useEffect(() => {
    if (!shouldShowOwnershipWarning) {
      setHasOwnershipFieldBlurred(false);
    }
  }, [shouldShowOwnershipWarning]);

  const handleFormFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement | null;
    if (target?.id === 'owner1OwnershipPercentage') {
      setIsOwnershipFieldFocused(true);
    }
  };

  const handleFormBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement | null;
    if (target?.id === 'owner1OwnershipPercentage') {
      setIsOwnershipFieldFocused(false);
      setHasOwnershipFieldBlurred(true);
    }
  };

  const selectedDocumentFiles = useMemo(() => {
    const fileKeys = Object.keys(files || {}).filter(
      (key) => key === 'bank-statements' || key.startsWith('bank-statements_')
    );
    return fileKeys.map((key) => files[key]).filter((file): file is File => !!file);
  }, [files]);

  const resolveUploadResults = (
    results: DocUploadAttempt['files'],
    successCount: number,
    failureCount: number
  ): DocUploadAttempt => {
    return {
      status: 'done',
      files: results,
      successCount,
      failureCount,
    };
  };

  const handleUploadMoreDocuments = () => {
    setDocUploadAttempt(defaultDocUploadAttempt);
  };

  // Safety check - although ApplicationForm should prevent this
  if (!structure?.theme || !currentPage?.sections) {
    return null;
  }

  const brandTheme = getApplicationBrandTheme(structure.theme, userShortId);

  // Filter out sections that have no viewable fields for the current signer
  const visibleSections = currentPage.sections.filter((section) =>
    section.fields.some((field) => viewableFields.includes(field.id))
  );

  const handleUploadDocs = async () => {
    let pendingFilesForAttempt: DocUploadFile[] = [];
    try {
      if (isUploadingDocs) return;

      if (!crmId) {
        toast.error('We need to save your application before uploading documents.');
        logger.warn('FormContent', 'Upload Docs blocked: missing crmId', {
          mappingShortId,
        });
        return;
      }

      if (!userShortId) {
        toast.error('Missing application owner identifier. Please refresh and try again.');
        logger.error('FormContent', 'Upload Docs blocked: missing userShortId', {
          mappingShortId,
        });
        return;
      }

      const selectedFiles = selectedDocumentFiles;

      if (!selectedFiles.length) {
        toast.error('Please add at least one document before uploading.');
        logger.warn('FormContent', 'Upload Docs blocked: no bank-statement files found', {
          mappingShortId,
        });
        return;
      }

      const pendingFiles: DocUploadFile[] = selectedFiles.map((file) => ({
        name: file.name,
        sizeBytes: file.size,
        status: 'pending',
      }));
      pendingFilesForAttempt = pendingFiles;

      setDocUploadAttempt({
        status: 'uploading',
        files: pendingFiles,
        successCount: 0,
        failureCount: 0,
      });
      handleFieldValueChange('bank-statements', []);
      fileService.clearFiles('bank-statements');

      logger.info('FormContent', 'Cleared document uploader after upload click', {
        mappingShortId,
        clearedCount: pendingFiles.length,
      });

      setIsUploadingDocs(true);
      logger.info('FormContent', 'Uploading bank statement documents from document-upload page', {
        mappingShortId,
        crmId,
        userShortId,
        fileCount: selectedFiles.length,
      });

      const result = await uploadBankStatements({
        userShortId,
        mappingShortId: mappingShortId || '',
        crmId,
        files: selectedFiles,
      });

      if (result.success) {
        const { successCount, failureCount, results } = result;
        const indexedResults = Array.isArray(results) ? results : [];
        const unresolvedResultsByName = new Map();

        indexedResults.forEach((item) => {
          if (!unresolvedResultsByName.has(item.fileName)) {
            unresolvedResultsByName.set(item.fileName, []);
          }
          unresolvedResultsByName.get(item.fileName).push(item);
        });

        const resolvedFiles = pendingFiles.map((file, index) => {
          const indexResult = indexedResults[index];
          const byNameQueue = unresolvedResultsByName.get(file.name) || [];
          const matchedResult =
            indexResult?.fileName === file.name ? indexResult : byNameQueue.shift();

          return {
            ...file,
            status: matchedResult?.status || (failureCount > 0 ? 'error' : 'success'),
            errorMessage: matchedResult?.errorMessage,
          };
        });

        logger.info('FormContent', 'Prepared document upload results for post-upload view', {
          mappingShortId,
          successCount,
          failureCount,
          totalFiles: resolvedFiles.length,
        });

        setDocUploadAttempt(resolveUploadResults(resolvedFiles, successCount, failureCount));

        if (successCount > 0 && failureCount === 0) {
          toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded.`);
        } else if (successCount > 0 && failureCount > 0) {
          toast.success(
            `${successCount} document${successCount > 1 ? 's' : ''} uploaded, ${failureCount} failed.`
          );
        } else {
          toast.error('No documents were uploaded. Please try again.');
        }
      } else {
        const erroredFiles = pendingFiles.map((file) => ({
          ...file,
          status: 'error' as const,
          errorMessage: 'Failed to upload file. Please try again.',
        }));
        logger.warn('FormContent', 'Upload Docs returned unsuccessful response', {
          mappingShortId,
          attemptedFiles: pendingFiles.length,
        });
        setDocUploadAttempt(resolveUploadResults(erroredFiles, 0, pendingFiles.length));
        toast.error('Failed to upload documents. Please try again.');
      }
    } catch (error) {
      const fallbackFiles = (
        pendingFilesForAttempt.length > 0
          ? pendingFilesForAttempt
          : selectedDocumentFiles.map((file) => ({
              name: file.name,
              sizeBytes: file.size,
              status: 'pending' as const,
            }))
      ).map((file) => ({
        ...file,
        status: 'error' as const,
        errorMessage: 'An unexpected error occurred while uploading.',
      }));
      setDocUploadAttempt(resolveUploadResults(fallbackFiles, 0, fallbackFiles.length));
      toast.error('An error occurred while uploading documents. Please try again.');
      logger.error('FormContent', 'Error during Upload Docs operation', {
        error,
        mappingShortId,
      });
    } finally {
      setIsUploadingDocs(false);
    }
  };

  const uploadAttemptTotal = docUploadAttempt.files.length;
  const uploadAttemptSuccessCount = docUploadAttempt.successCount;
  const uploadAttemptFailureCount = docUploadAttempt.failureCount;
  const isUploadAttemptMixed = uploadAttemptSuccessCount > 0 && uploadAttemptFailureCount > 0;
  const isUploadAttemptError = uploadAttemptSuccessCount === 0 && uploadAttemptFailureCount > 0;
  const uploadAttemptHeading = isUploadAttemptError
    ? 'Documents failed to upload'
    : `${uploadAttemptSuccessCount} document${
        uploadAttemptSuccessCount === 1 ? '' : 's'
      } uploaded successfully`;
  const uploadAttemptSubheading =
    docUploadAttempt.status === 'uploading'
      ? 'Please wait while we attach your documents.'
      : isUploadAttemptMixed
        ? `${uploadAttemptFailureCount} failed. Would you like to upload more?`
        : isUploadAttemptError
          ? 'Please try again.'
          : 'Would you like to upload more?';

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <ApplicationSidebar />

      {/* Mobile Progress Header */}
      <MobileProgressHeader />

      {/* Main Content */}
      <div className="flex-1 ml-0 md:ml-80 lg:ml-72 xl:ml-96 transition-all duration-300 font-inter flex flex-col">
        {/* Invisible spacer to match logo height */}
        <div className="bg-white hidden md:block h-32" />

        <div
          ref={formRef}
          onFocusCapture={handleFormFocusCapture}
          onBlurCapture={handleFormBlurCapture}
          className={`bg-white px-4 py-4 sm:px-6 sm:py-6 md:py-8 md:pt-6 mt-24 md:mt-0 flex-1 flex flex-col transition-all ease-out md:pl-12 ${
            isInitialLoad ? 'duration-500' : 'duration-700'
          } ${
            isVisible
              ? 'opacity-100 translate-x-0'
              : isInitialLoad
                ? 'opacity-0 -translate-x-1'
                : 'opacity-0 -translate-x-2'
          }`}
        >
          {/* Form Content */}
          <div className="min-h-0">
            {/* Ownership warning banner on Owners page for primary signer (hidden in pdfOnly mode) */}
            {shouldRenderOwnershipWarning && !pdfOnly && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    <span className="font-semibold">
                      Total Ownership listed on the application is less than 51%.
                    </span>
                  </div>
                  <p className="sm:ml-8">
                    Please fix your ownership details below or add your co-owner / partner as an
                    applicant using the additional owner section below.
                  </p>
                </div>
              </div>
            )}

            {/* Add/Manage Owner button on Owners page for primary signer (hidden in pdfOnly mode) */}
            {isOwnershipPage && isPrimarySigner && !pdfOnly && (
              <>
                <div className="mb-4 flex justify-start">
                  <button
                    type="button"
                    onClick={openSignersModal}
                    className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:brightness-95"
                    style={{
                      backgroundColor: brandTheme.actionSubtleBackground,
                      color: brandTheme.surfaceBackground,
                    }}
                  >
                    <span className="mr-2 text-lg leading-none">+</span>
                    <span>Add Owner</span>
                    {activeAdditionalOwnersCount > 0 && (
                      <span className="ml-2 inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full border border-border bg-background px-2 text-xs font-semibold text-foreground">
                        {activeAdditionalOwnersCount}
                      </span>
                    )}
                  </button>
                </div>
              </>
            )}

            {(!isDocumentUploadPage || !isPostUploadView) &&
              visibleSections.map((section) => <FormSection key={section.id} section={section} />)}

            {isDocumentUploadPage && isPostUploadView && (
              <div className="mb-8">
                <div className="flex flex-col items-center text-center gap-3">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full ${
                      isUploadAttemptError
                        ? 'bg-destructive/10'
                        : isUploadAttemptLoading
                          ? 'bg-muted'
                          : 'bg-success/10'
                    }`}
                  >
                    {isUploadAttemptLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : isUploadAttemptError ? (
                      <AlertTriangle className="h-6 w-6 text-destructive" />
                    ) : (
                      <CheckCircle className="h-6 w-6 text-success" />
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      {docUploadAttempt.status === 'uploading'
                        ? 'Uploading documents...'
                        : uploadAttemptHeading}
                    </p>
                    <p className="text-xs text-muted-foreground">{uploadAttemptSubheading}</p>
                  </div>
                </div>

                {docUploadAttempt.status === 'done' && uploadAttemptTotal > 0 && (
                  <div className="mt-6 space-y-3">
                    {docUploadAttempt.files.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className={`flex items-center justify-between rounded-lg border p-3 ${
                          file.status === 'error'
                            ? 'border-destructive bg-destructive/10'
                            : 'border-success bg-success/10'
                        }`}
                      >
                        <div className="flex flex-col text-left">
                          <span className="text-sm text-foreground truncate">{file.name}</span>
                          {file.status === 'error' && (
                            <span className="text-xs text-destructive">
                              {file.errorMessage || 'Upload failed'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {docUploadAttempt.status === 'done' && (
                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      onClick={handleUploadMoreDocuments}
                      className="rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-200 hover:brightness-95"
                      style={{
                        backgroundColor: brandTheme.actionBackground,
                        color: brandTheme.actionForeground,
                      }}
                    >
                      Upload More Documents
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Contact Consent Field on the first page */}
            {isFirstPage && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col lg:flex-row justify-start">
                  <div className="lg:w-64 flex-shrink-0"></div>
                  <div className="flex-1 lg:pl-8 max-w-4xl">
                    <ContactConsentField
                      onChange={handleContactConsentChange}
                      value={contactConsent}
                      isInvalid={isContactConsentInvalid}
                      legalContext={legalContext}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Terms Consent Field on the last page */}
            {isLastPage && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col lg:flex-row justify-start">
                  <div className="lg:w-64 flex-shrink-0"></div>
                  <div className="flex-1 lg:pl-8 max-w-4xl">
                    <TermsConsentField
                      onChange={handleTermsConsentChange}
                      value={termsConsent}
                      isInvalid={isTermsInvalid}
                      legalContext={legalContext}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Document Upload Actions - only on Document Upload page */}
          {isDocumentUploadPage && !isPostUploadView && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-row justify-start">
                <div className="lg:w-64 flex-shrink-0"></div>
                <div className="flex-1 lg:pl-8 max-w-4xl">
                  <button
                    type="button"
                    onClick={handleUploadDocs}
                    disabled={isUploadingDocs || selectedDocumentFiles.length === 0}
                    className={`flex items-center justify-center text-sm w-[140px] md:w-[160px] px-6 py-2.5 rounded-full transition-all duration-200 font-medium hover:brightness-95 hover:shadow-lg ${
                      isUploadingDocs || selectedDocumentFiles.length === 0
                        ? 'opacity-70 cursor-not-allowed'
                        : ''
                    }`}
                    style={{
                      backgroundColor: brandTheme.actionBackground,
                      color: brandTheme.actionForeground,
                    }}
                  >
                    {isUploadingDocs ? 'Uploading...' : 'Upload Docs'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Section - hidden on Document Upload page */}
          {!isDocumentUploadPage && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-row justify-start">
                <div className="lg:w-64 flex-shrink-0"></div>
                <div className="flex-1 lg:pl-8 max-w-4xl">
                  <FormNavigation
                    activePage={activePage}
                    totalPages={applicationTotalPages}
                    theme={structure.theme}
                    onNext={() => handleNextPageWithValidation(isLastPage, isFirstPage, formRef)}
                    onBack={handlePreviousPageNavigation}
                    mappingId={mappingShortId}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Owners Modal - Signatures page flow */}
      {showSignersModal && userShortId && mappingShortId && crmId && (
        <SignersModal
          isOpen={showSignersModal}
          onClose={closeSignersModal}
          onConfirm={addOptimisticSigners}
          userShortId={userShortId}
          mappingShortId={mappingShortId}
          crmId={crmId}
          prefetchedSigners={activeAdditionalOwnerSigners}
          hasPrefetchedSigners={hasPrefetchedSigners}
          isPrefetchingSigners={isLoadingSigners}
        />
      )}
    </div>
  );
};
